import { fromJS, List, Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import { Cursor } from 'decap-cms-lib-util';
import { selectCollectionEntriesCursor } from '../reducers/cursors';
import { selectFields, updateFieldByKey, selectSortDataPath, selectDefaultSortField } from '../reducers/collections';
import { selectIntegration, selectPublishedSlugs } from '../reducers';
import { getIntegrationProvider } from '../integrations';
import { currentBackend } from '../backend';
import { serializeValues } from '../lib/serializeEntryValues';
import { createEntry } from '../valueObjects/Entry';
import { createAssetProxy } from '../valueObjects/AssetProxy';
import ValidationErrorTypes from '../constants/validationErrorTypes';
import { addAssets, getAsset } from './media';
import { SortDirection } from '../types/redux';
import { waitForMediaLibraryToLoad, loadMedia } from './mediaLibrary';
import { waitUntil } from './waitUntil';
import { selectIsFetching, selectEntriesSortFields, selectEntryByPath } from '../reducers/entries';
import { navigateToEntry } from '../routing/history';
import { getProcessSegment } from '../lib/formatters';
import { hasI18n, duplicateDefaultI18nFields, serializeI18n, I18N, I18N_FIELD } from '../lib/i18n';
import { isPaginationEnabled } from '../lib/pagination';
import { getCachedEntries, setCachedEntries, invalidateCollectionCache } from '../lib/entryCache';
import { hasActiveFilters, hasActiveGroups, hasActiveSorts, extractActiveFilters, matchesFilters, getFieldValue } from '../lib/entryHelpers';
import { addNotification } from './notifications';
import { selectCustomPath } from '../reducers/entryDraft';
/*
 * Constant Declarations
 */
export const ENTRY_REQUEST = 'ENTRY_REQUEST';
export const ENTRY_SUCCESS = 'ENTRY_SUCCESS';
export const ENTRY_FAILURE = 'ENTRY_FAILURE';
export const ENTRIES_REQUEST = 'ENTRIES_REQUEST';
export const ENTRIES_SUCCESS = 'ENTRIES_SUCCESS';
export const ENTRIES_FAILURE = 'ENTRIES_FAILURE';
export const ENTRIES_PROGRESS = 'ENTRIES_PROGRESS';
export const SORT_ENTRIES_REQUEST = 'SORT_ENTRIES_REQUEST';
export const SORT_ENTRIES_SUCCESS = 'SORT_ENTRIES_SUCCESS';
export const SORT_ENTRIES_FAILURE = 'SORT_ENTRIES_FAILURE';
export const FILTER_ENTRIES_REQUEST = 'FILTER_ENTRIES_REQUEST';
export const FILTER_ENTRIES_SUCCESS = 'FILTER_ENTRIES_SUCCESS';
export const FILTER_ENTRIES_FAILURE = 'FILTER_ENTRIES_FAILURE';
export const GROUP_ENTRIES_REQUEST = 'GROUP_ENTRIES_REQUEST';
export const GROUP_ENTRIES_SUCCESS = 'GROUP_ENTRIES_SUCCESS';
export const GROUP_ENTRIES_FAILURE = 'GROUP_ENTRIES_FAILURE';
export const DRAFT_CREATE_FROM_ENTRY = 'DRAFT_CREATE_FROM_ENTRY';
export const DRAFT_CREATE_EMPTY = 'DRAFT_CREATE_EMPTY';
export const DRAFT_DISCARD = 'DRAFT_DISCARD';
export const DRAFT_CHANGE_FIELD = 'DRAFT_CHANGE_FIELD';
export const DRAFT_VALIDATION_ERRORS = 'DRAFT_VALIDATION_ERRORS';
export const DRAFT_CLEAR_ERRORS = 'DRAFT_CLEAR_ERRORS';
export const DRAFT_LOCAL_BACKUP_RETRIEVED = 'DRAFT_LOCAL_BACKUP_RETRIEVED';
export const DRAFT_CREATE_FROM_LOCAL_BACKUP = 'DRAFT_CREATE_FROM_LOCAL_BACKUP';
export const DRAFT_CREATE_DUPLICATE_FROM_ENTRY = 'DRAFT_CREATE_DUPLICATE_FROM_ENTRY';
export const ENTRY_PERSIST_REQUEST = 'ENTRY_PERSIST_REQUEST';
export const ENTRY_PERSIST_SUCCESS = 'ENTRY_PERSIST_SUCCESS';
export const ENTRY_PERSIST_FAILURE = 'ENTRY_PERSIST_FAILURE';
export const ENTRY_DELETE_REQUEST = 'ENTRY_DELETE_REQUEST';
export const ENTRY_DELETE_SUCCESS = 'ENTRY_DELETE_SUCCESS';
export const ENTRY_DELETE_FAILURE = 'ENTRY_DELETE_FAILURE';
export const ADD_DRAFT_ENTRY_MEDIA_FILE = 'ADD_DRAFT_ENTRY_MEDIA_FILE';
export const REMOVE_DRAFT_ENTRY_MEDIA_FILE = 'REMOVE_DRAFT_ENTRY_MEDIA_FILE';
export const CHANGE_VIEW_STYLE = 'CHANGE_VIEW_STYLE';
export const SET_ENTRIES_PAGE_SIZE = 'SET_ENTRIES_PAGE_SIZE';
export const LOAD_ENTRIES_PAGE = 'LOAD_ENTRIES_PAGE';
export const SET_ENTRIES_PAGE = 'SET_ENTRIES_PAGE';

/*
 * Simple Action Creators (Internal)
 * We still need to export them for tests
 */
export function entryLoading(collection, slug) {
  return {
    type: ENTRY_REQUEST,
    payload: {
      collection: collection.get('name'),
      slug
    }
  };
}
export function entryLoaded(collection, entry) {
  return {
    type: ENTRY_SUCCESS,
    payload: {
      collection: collection.get('name'),
      entry
    }
  };
}
export function entryLoadError(error, collection, slug) {
  return {
    type: ENTRY_FAILURE,
    payload: {
      error,
      collection: collection.get('name'),
      slug
    }
  };
}
export function entriesLoading(collection) {
  return {
    type: ENTRIES_REQUEST,
    payload: {
      collection: collection.get('name')
    }
  };
}
export function entriesLoaded(collection, entries, pagination, cursor, append = true, hasMore = false) {
  return {
    type: ENTRIES_SUCCESS,
    payload: {
      collection: collection.get('name'),
      entries,
      page: pagination,
      cursor: Cursor.create(cursor),
      append,
      hasMore
    }
  };
}
export function entriesFailed(collection, error) {
  return {
    type: ENTRIES_FAILURE,
    error: 'Failed to load entries',
    payload: error.toString(),
    meta: {
      collection: collection.get('name')
    }
  };
}
export function entriesProgress(collection, loadedCount, totalCount) {
  return {
    type: ENTRIES_PROGRESS,
    payload: {
      collection: collection.get('name'),
      loadedCount,
      totalCount,
      percentage: totalCount > 0 ? Math.round(loadedCount / totalCount * 100) : 0
    }
  };
}
export function setEntriesPageSize(collection, pageSize) {
  return {
    type: SET_ENTRIES_PAGE_SIZE,
    payload: {
      collection: collection.get('name'),
      pageSize
    }
  };
}
export function setEntriesPage(collection, page) {
  return {
    type: SET_ENTRIES_PAGE,
    payload: {
      collection: collection.get('name'),
      page
    }
  };
}
export function loadEntriesPage(collection, page) {
  return async (dispatch, getState) => {
    const state = getState();
    const collectionName = collection.get('name');

    // Check if sorting is active (sortedIds exists)
    const sortedIds = state.entries.getIn(['pages', collectionName, 'sortedIds']);
    if (sortedIds) {
      // Client-side pagination: just update the page index
      dispatch(setEntriesPage(collection, page));
    } else {
      // Server-side pagination: load new entries from backend
      dispatch(loadEntries(collection, page));
    }
  };
}
export async function getAllEntries(state, collection, onProgress) {
  const backend = currentBackend(state.config);
  const integration = selectIntegration(state, collection.get('name'), 'listEntries');
  const provider = integration ? getIntegrationProvider(state.integrations, backend.getToken, integration) : backend;
  const entries = await provider.listAllEntries(collection, onProgress);
  return entries;
}
export function sortByField(collection, key, direction = SortDirection.Ascending) {
  return async (dispatch, getState) => {
    const state = getState();
    const collectionName = collection.get('name');
    // if we're already fetching we update the sort key, but skip loading entries
    const isFetching = selectIsFetching(state.entries, collectionName);
    dispatch({
      type: SORT_ENTRIES_REQUEST,
      payload: {
        collection: collectionName,
        key,
        direction
      }
    });
    if (isFetching) {
      return;
    }
    try {
      let entries = await getAllEntries(state, collection);

      // Check if filtering is active - if so, apply filters first
      const activeFilters = state.entries.getIn(['filter', collectionName]);
      if (hasActiveFilters(activeFilters)) {
        const filters = extractActiveFilters(activeFilters);
        entries = entries.filter(entry => matchesFilters(entry, filters));
      }

      // Sort entries by the specified field
      const dataPath = selectSortDataPath(collection, key);
      const order = direction === SortDirection.Ascending ? 'asc' : 'desc';
      const sortedEntries = orderBy(entries, [entry => {
        const value = getFieldValue(entry, dataPath);
        // Handle case-insensitive string sorting
        return typeof value === 'string' ? value.toLowerCase() : value;
      }], [order]);

      // Check if grouping is active - if so, use GROUP_ENTRIES_SUCCESS to avoid pagination
      const activeGroups = state.entries.getIn(['group', collectionName]);
      if (hasActiveGroups(activeGroups)) {
        dispatch({
          type: GROUP_ENTRIES_SUCCESS,
          payload: {
            collection: collectionName,
            entries: sortedEntries
          }
        });
      } else {
        dispatch({
          type: SORT_ENTRIES_SUCCESS,
          payload: {
            collection: collectionName,
            key,
            direction,
            entries: sortedEntries
          }
        });
      }
    } catch (error) {
      dispatch({
        type: SORT_ENTRIES_FAILURE,
        payload: {
          collection: collection.get('name'),
          key,
          direction,
          error
        }
      });
    }
  };
}
export function filterByField(collection, filter) {
  return async (dispatch, getState) => {
    const state = getState();
    // if we're already fetching we update the filter key, but skip loading entries
    const isFetching = selectIsFetching(state.entries, collection.get('name'));

    // Check if this filter is currently active (to detect if we're disabling it)
    const currentFilter = state.entries.getIn(['filter', collection.get('name'), filter.id]);
    const isCurrentlyActive = currentFilter && typeof currentFilter.get === 'function' ? currentFilter.get('active') === true : false;
    dispatch({
      type: FILTER_ENTRIES_REQUEST,
      payload: {
        collection: collection.get('name'),
        filter
      }
    });
    if (isFetching) {
      return;
    }
    try {
      // If we're disabling the last active filter, reload with pagination
      const allFilters = state.entries.getIn(['filter', collection.get('name')]);
      let hasOtherActiveFilters = false;
      if (allFilters && typeof allFilters.some === 'function') {
        hasOtherActiveFilters = allFilters.some(f => f?.get('id') !== filter.id && f?.get('active') === true);
      }
      if (isCurrentlyActive && !hasOtherActiveFilters) {
        // Disabling filtering - reload entries with pagination
        return dispatch(loadEntries(collection));
      }

      // Enabling filtering or switching filters - load all entries and filter them
      const allEntries = await getAllEntries(state, collection);

      // Get the new filter state (it was toggled in FILTER_ENTRIES_REQUEST)
      const updatedState = getState();
      const updatedFilters = updatedState.entries.getIn(['filter', collection.get('name')]);

      // Apply all active filters
      const filteredEntries = allEntries.filter(entry => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const activeFilters = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (updatedFilters && typeof updatedFilters.forEach === 'function') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedFilters.forEach(f => {
            if (f.get('active') === true) {
              activeFilters.push({
                pattern: f.get('pattern'),
                field: f.get('field')
              });
            }
          });
        }

        // Entry must match all active filters
        return activeFilters.every(({
          pattern,
          field
        }) => {
          const data = entry.data || {};
          const fieldParts = field.split('.');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let value = data;
          for (const part of fieldParts) {
            value = value?.[part];
          }
          const matched = value !== undefined && new RegExp(String(pattern)).test(String(value));
          return matched;
        });
      });

      // Check if sorting is active - if so, apply sort after filtering
      const activeSorts = updatedState.entries.getIn(['sort', collection.get('name')]);
      let finalEntries = filteredEntries;
      if (hasActiveSorts(activeSorts)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sortField = activeSorts.valueSeq().first();
        const sortKey = sortField.get('key');
        const sortDirection = sortField.get('direction');
        const dataPath = selectSortDataPath(collection, sortKey);
        const order = sortDirection === SortDirection.Ascending ? 'asc' : 'desc';
        finalEntries = orderBy(filteredEntries, [entry => {
          const value = getFieldValue(entry, dataPath);
          return typeof value === 'string' ? value.toLowerCase() : value;
        }], [order]);
      }

      // Check if grouping is active - if so, use GROUP_ENTRIES_SUCCESS to avoid pagination
      const activeGroups = updatedState.entries.getIn(['group', collection.get('name')]);
      if (hasActiveGroups(activeGroups)) {
        dispatch({
          type: GROUP_ENTRIES_SUCCESS,
          payload: {
            collection: collection.get('name'),
            entries: finalEntries
          }
        });
      } else {
        dispatch({
          type: FILTER_ENTRIES_SUCCESS,
          payload: {
            collection: collection.get('name'),
            filter,
            entries: finalEntries
          }
        });
      }
    } catch (error) {
      dispatch({
        type: FILTER_ENTRIES_FAILURE,
        payload: {
          collection: collection.get('name'),
          filter,
          error
        }
      });
    }
  };
}
export function groupByField(collection, group) {
  return async (dispatch, getState) => {
    const state = getState();
    const isFetching = selectIsFetching(state.entries, collection.get('name'));

    // Check if this group is currently active (to detect if we're disabling it)
    const currentGroup = state.entries.getIn(['group', collection.get('name'), group.id]);
    const isCurrentlyActive = currentGroup && typeof currentGroup.get === 'function' ? currentGroup.get('active') === true : false;
    dispatch({
      type: GROUP_ENTRIES_REQUEST,
      payload: {
        collection: collection.get('name'),
        group
      }
    });
    if (isFetching) {
      return;
    }
    try {
      // If we're disabling the last active group, reload with pagination
      const allGroups = state.entries.getIn(['group', collection.get('name')]);
      let hasOtherActiveGroups = false;
      if (allGroups && typeof allGroups.some === 'function') {
        hasOtherActiveGroups = allGroups.some(g => g?.get('id') !== group.id && g?.get('active') === true);
      }
      if (isCurrentlyActive && !hasOtherActiveGroups) {
        // Disabling grouping - reload entries with pagination
        return dispatch(loadEntries(collection));
      }

      // Enabling grouping or switching groups - load all entries
      let entries = await getAllEntries(state, collection);

      // Get the updated state after GROUP_ENTRIES_REQUEST
      const updatedState = getState();

      // Check if filtering is active - if so, apply filters
      const activeFilters = updatedState.entries.getIn(['filter', collection.get('name')]);
      if (hasActiveFilters(activeFilters)) {
        const filters = extractActiveFilters(activeFilters);
        entries = entries.filter(entry => matchesFilters(entry, filters));
      }

      // Check if sorting is active - if so, apply sort
      const activeSorts = updatedState.entries.getIn(['sort', collection.get('name')]);
      if (hasActiveSorts(activeSorts)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sortField = activeSorts.valueSeq().first();
        const sortKey = sortField.get('key');
        const sortDirection = sortField.get('direction');
        const dataPath = selectSortDataPath(collection, sortKey);
        const order = sortDirection === SortDirection.Ascending ? 'asc' : 'desc';
        entries = orderBy(entries, [entry => {
          const value = getFieldValue(entry, dataPath);
          return typeof value === 'string' ? value.toLowerCase() : value;
        }], [order]);
      }
      dispatch({
        type: GROUP_ENTRIES_SUCCESS,
        payload: {
          collection: collection.get('name'),
          group,
          entries
        }
      });
    } catch (error) {
      dispatch({
        type: GROUP_ENTRIES_FAILURE,
        payload: {
          collection: collection.get('name'),
          group,
          error
        }
      });
    }
  };
}
export function changeViewStyle(viewStyle) {
  return {
    type: CHANGE_VIEW_STYLE,
    payload: {
      style: viewStyle
    }
  };
}
export function entryPersisting(collection, entry) {
  return {
    type: ENTRY_PERSIST_REQUEST,
    payload: {
      collectionName: collection.get('name'),
      entrySlug: entry.get('slug')
    }
  };
}
export function entryPersisted(collection, entry, slug) {
  return {
    type: ENTRY_PERSIST_SUCCESS,
    payload: {
      collectionName: collection.get('name'),
      entrySlug: entry.get('slug'),
      /**
       * Pass slug from backend for newly created entries.
       */
      slug,
      /**
       * Pass the persisted entry so we can update the cache.
       */
      entry
    }
  };
}
export function entryPersistFail(collection, entry, error) {
  return {
    type: ENTRY_PERSIST_FAILURE,
    error: 'Failed to persist entry',
    payload: {
      collectionName: collection.get('name'),
      entrySlug: entry.get('slug'),
      error: error.toString()
    }
  };
}
export function entryDeleting(collection, slug) {
  return {
    type: ENTRY_DELETE_REQUEST,
    payload: {
      collectionName: collection.get('name'),
      entrySlug: slug
    }
  };
}
export function entryDeleted(collection, slug) {
  return {
    type: ENTRY_DELETE_SUCCESS,
    payload: {
      collectionName: collection.get('name'),
      entrySlug: slug
    }
  };
}
export function entryDeleteFail(collection, slug, error) {
  return {
    type: ENTRY_DELETE_FAILURE,
    payload: {
      collectionName: collection.get('name'),
      entrySlug: slug,
      error: error.toString()
    }
  };
}
export function emptyDraftCreated(entry) {
  return {
    type: DRAFT_CREATE_EMPTY,
    payload: entry
  };
}
/*
 * Exported simple Action Creators
 */
export function createDraftFromEntry(entry) {
  return {
    type: DRAFT_CREATE_FROM_ENTRY,
    payload: {
      entry
    }
  };
}
export function draftDuplicateEntry(entry) {
  return {
    type: DRAFT_CREATE_DUPLICATE_FROM_ENTRY,
    payload: createEntry(entry.get('collection'), '', '', {
      data: entry.get('data'),
      meta: entry.get('meta').toJS(),
      i18n: entry.get('i18n').toJS(),
      mediaFiles: entry.get('mediaFiles').toJS()
    })
  };
}
export function discardDraft() {
  return {
    type: DRAFT_DISCARD
  };
}
export function changeDraftField({
  field,
  value,
  metadata,
  entries,
  i18n
}) {
  return {
    type: DRAFT_CHANGE_FIELD,
    payload: {
      field,
      value,
      metadata,
      entries,
      i18n
    }
  };
}
export function changeDraftFieldValidation(uniquefieldId, errors) {
  return {
    type: DRAFT_VALIDATION_ERRORS,
    payload: {
      uniquefieldId,
      errors
    }
  };
}
export function clearFieldErrors(uniqueFieldId) {
  return {
    type: DRAFT_CLEAR_ERRORS,
    payload: {
      uniqueFieldId
    }
  };
}
export function localBackupRetrieved(entry) {
  return {
    type: DRAFT_LOCAL_BACKUP_RETRIEVED,
    payload: {
      entry
    }
  };
}
export function loadLocalBackup() {
  return {
    type: DRAFT_CREATE_FROM_LOCAL_BACKUP
  };
}
export function addDraftEntryMediaFile(file) {
  return {
    type: ADD_DRAFT_ENTRY_MEDIA_FILE,
    payload: file
  };
}
export function removeDraftEntryMediaFile({
  id
}) {
  return {
    type: REMOVE_DRAFT_ENTRY_MEDIA_FILE,
    payload: {
      id
    }
  };
}
export function persistLocalBackup(entry, collection) {
  return (_dispatch, getState) => {
    const state = getState();
    const backend = currentBackend(state.config);
    return backend.persistLocalDraftBackup(entry, collection);
  };
}
export function createDraftDuplicateFromEntry(entry) {
  return dispatch => {
    dispatch(waitUntil({
      predicate: ({
        type
      }) => type === DRAFT_CREATE_EMPTY,
      run: () => dispatch(draftDuplicateEntry(entry))
    }));
  };
}
export function retrieveLocalBackup(collection, slug) {
  return async (dispatch, getState) => {
    const state = getState();
    const backend = currentBackend(state.config);
    const {
      entry
    } = await backend.getLocalDraftBackup(collection, slug);
    if (entry) {
      // load assets from backup
      const mediaFiles = entry.mediaFiles || [];
      const assetProxies = await Promise.all(mediaFiles.map(file => {
        if (file.file || file.url) {
          return createAssetProxy({
            path: file.path,
            file: file.file,
            url: file.url,
            field: file.field
          });
        } else {
          return getAsset({
            collection,
            entry: fromJS(entry),
            path: file.path,
            field: file.field
          })(dispatch, getState);
        }
      }));
      dispatch(addAssets(assetProxies));
      return dispatch(localBackupRetrieved(entry));
    }
  };
}
export function deleteLocalBackup(collection, slug) {
  return (_dispatch, getState) => {
    const state = getState();
    const backend = currentBackend(state.config);
    return backend.deleteLocalDraftBackup(collection, slug);
  };
}

/*
 * Exported Thunk Action Creators
 */

export function loadEntry(collection, slug) {
  return async (dispatch, getState) => {
    await waitForMediaLibraryToLoad(dispatch, getState());
    dispatch(entryLoading(collection, slug));
    try {
      const loadedEntry = await tryLoadEntry(getState(), collection, slug);
      dispatch(entryLoaded(collection, loadedEntry));
      dispatch(createDraftFromEntry(loadedEntry));
    } catch (error) {
      dispatch(addNotification({
        message: {
          details: error.message,
          key: 'ui.toast.onFailToLoadEntries'
        },
        type: 'error',
        dismissAfter: 8000
      }));
      dispatch(entryLoadError(error, collection, slug));
    }
  };
}
export async function tryLoadEntry(state, collection, slug) {
  const backend = currentBackend(state.config);
  const loadedEntry = await backend.getEntry(state, collection, slug);
  return loadedEntry;
}
const appendActions = fromJS({
  ['append_next']: {
    action: 'next',
    append: true
  }
});
function addAppendActionsToCursor(cursor) {
  const originalActions = cursor.actions?.toArray() || [];
  console.log(`[addAppendActionsToCursor] Original cursor actions: ${JSON.stringify(originalActions)}`);
  const updatedCursor = Cursor.create(cursor).updateStore('actions', actions => {
    return actions.union(appendActions.filter(v => actions.has(v.get('action'))).keySeq());
  });
  const newActions = updatedCursor.actions?.toArray() || [];
  console.log(`[addAppendActionsToCursor] Updated cursor actions: ${JSON.stringify(newActions)}`);
  return updatedCursor;
}
export function loadEntries(collection, page = 0) {
  return async (dispatch, getState) => {
    if (collection.get('isFetching')) {
      return;
    }
    const state = getState();
    const collectionName = collection.get('name');

    // If user has already set a sort, use it
    const sortFields = selectEntriesSortFields(state.entries, collectionName);

    // If user has already set a sort, use it
    if (sortFields && sortFields.length > 0) {
      const field = sortFields[0];
      return dispatch(sortByField(collection, field.get('key'), field.get('direction')));
    }

    // Otherwise, check for a default sort field in the collection configuration
    const defaultSort = selectDefaultSortField(collection);
    if (defaultSort) {
      const direction = defaultSort.direction === 'desc' ? SortDirection.Descending : SortDirection.Ascending;
      return dispatch(sortByField(collection, defaultSort.field, direction));
    }
    const backend = currentBackend(state.config);
    const integration = selectIntegration(state, collectionName, 'listEntries');
    const provider = integration ? getIntegrationProvider(state.integrations, backend.getToken, integration) : backend;
    // In new pagination mode, page navigation replaces items; append applies only to old/infinite modes
    const paginationEnabled = isPaginationEnabled(collection, state.config);
    const append = paginationEnabled ? false : !!(page && !isNaN(page) && page > 0);
    dispatch(entriesLoading(collection));
    try {
      const isNestedCollection = collection.has('nested');
      const isI18nCollection = hasI18n(collection);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const backendConfig = state.config?.backend;
      const useGraphQL = backendConfig?.use_graphql === true;

      // For GraphQL, nested collections, or i18n collections, load all entries
      // GraphQL will handle batching automatically via listFilesPaginated with progressive loading
      const loadAllEntries = isNestedCollection || isI18nCollection || useGraphQL;
      console.log(`[loadEntries] ${collectionName}: isNested=${isNestedCollection}, isI18n=${isI18nCollection}, useGraphQL=${useGraphQL}, loadAllEntries=${loadAllEntries}`);

      // Try cache first for collections that load all entries
      let cachedEntries = null;
      if (loadAllEntries) {
        cachedEntries = await getCachedEntries(collectionName);
      }
      if (cachedEntries) {
        // Use cached entries
        console.log(`[loadEntries] Using cached entries for ${collectionName}`);
        const response = {
          entries: cachedEntries,
          cursor: Cursor.create({}),
          pagination: 0
        };
        dispatch(entriesLoaded(collection, response.entries, response.pagination, addAppendActionsToCursor(response.cursor), append, false));

        // For i18n collections with pagination enabled, set up client-side pagination using sortedIds
        if (isI18nCollection && paginationEnabled) {
          dispatch({
            type: SORT_ENTRIES_SUCCESS,
            payload: {
              collection: collectionName,
              entries: response.entries
            }
          });
        }
      } else if (isNestedCollection) {
        // For nested collections, we MUST load ALL entries at once with proper depth handling
        // to build the navigation tree. Use getAllEntries which calls allEntriesByFolder.
        console.log(`[loadEntries] Loading all entries for nested collection: ${collectionName}`);
        const allEntries = await getAllEntries(state, collection);
        console.log(`[loadEntries] getAllEntries returned ${allEntries.length} entries for ${collectionName}`);
        dispatch(entriesLoaded(collection, allEntries, 0, Cursor.create({}), false, false));

        // Cache all entries after loading
        await setCachedEntries(collectionName, allEntries);
      } else if (isI18nCollection) {
        // For i18n collections, we MUST load ALL entries at once and process them together
        // so that i18n grouping works correctly (entries in different locales get grouped).
        // Use getAllEntries which calls listAllEntries that processes all entries together.
        console.log(`[loadEntries] Loading all entries for i18n collection: ${collectionName}`);
        const allEntries = await getAllEntries(state, collection, progress => {
          // Dispatch progress update
          dispatch(entriesProgress(collection, progress.loadedCount, progress.totalCount));

          // Show entries progressively as they load (only if not the final batch)
          if (progress.loadedCount < progress.totalCount) {
            dispatch(entriesLoaded(collection, progress.entries, 0, Cursor.create({}), false, false));
          }
        });
        console.log(`[loadEntries] getAllEntries returned ${allEntries.length} entries for ${collectionName}`);
        dispatch(entriesLoaded(collection, allEntries, 0, Cursor.create({}), false, false));

        // Cache all entries after loading
        await setCachedEntries(collectionName, allEntries);

        // For i18n collections with pagination enabled, set up client-side pagination using sortedIds
        if (paginationEnabled) {
          dispatch({
            type: SORT_ENTRIES_SUCCESS,
            payload: {
              collection: collectionName,
              entries: allEntries
            }
          });
        }
      } else if (useGraphQL && loadAllEntries) {
        // For GraphQL collections, load ALL entries progressively in batches
        // GraphQL handles batching automatically via listFilesPaginated
        console.log(`[loadEntries] Loading all entries for GraphQL collection: ${collectionName}`);
        const allEntries = await getAllEntries(state, collection, progress => {
          // Dispatch progress update
          dispatch(entriesProgress(collection, progress.loadedCount, progress.totalCount));

          // Show entries progressively as they load (only if not the final batch)
          if (progress.loadedCount < progress.totalCount) {
            dispatch(entriesLoaded(collection, progress.entries, 0, Cursor.create({}), false, false));
          }
        });
        console.log(`[loadEntries] getAllEntries returned ${allEntries.length} entries for ${collectionName}`);
        dispatch(entriesLoaded(collection, allEntries, 0, Cursor.create({}), false, false));

        // Cache all entries after loading
        await setCachedEntries(collectionName, allEntries);
      } else {
        // Regular pagination mode - load single page
        let response = await provider.listEntries(collection, page);
        response = {
          ...response,
          cursor: integration ? Cursor.create({
            actions: ['next'],
            meta: {
              usingOldPaginationAPI: true
            },
            data: {
              nextPage: page + 1
            }
          }) : Cursor.create(response.cursor)
        };
        const usingOld = response.cursor.meta.get('usingOldPaginationAPI');
        const hasMore = response.cursor.actions.has('next');
        dispatch(entriesLoaded(collection, usingOld ? response.entries.reverse() : response.entries, response.pagination, addAppendActionsToCursor(response.cursor), append, hasMore));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      dispatch(addNotification({
        message: {
          details: err,
          key: 'ui.toast.onFailToLoadEntries'
        },
        type: 'error',
        dismissAfter: 8000
      }));
      return Promise.reject(dispatch(entriesFailed(collection, error)));
    }
  };
}
function traverseCursor(backend, cursor, action) {
  if (!cursor.actions.has(action)) {
    throw new Error(`The current cursor does not support the pagination action "${action}".`);
  }
  return backend.traverseCursor(cursor, action);
}
export function traverseCollectionCursor(collection, action) {
  return async (dispatch, getState) => {
    const state = getState();
    const collectionName = collection.get('name');
    if (state.entries.getIn(['pages', `${collectionName}`, 'isFetching'])) {
      return;
    }
    const backend = currentBackend(state.config);
    const cursor = selectCollectionEntriesCursor(state.cursors, collection.get('name'));

    // Check if action is directly in appendActions (e.g., 'append_next')
    // OR if we need to find the append action that maps to this action (e.g., 'next' → 'append_next')
    let realAction = action;
    let append = false;
    if (appendActions.has(action)) {
      // Direct match: action is 'append_next'
      const mapping = appendActions.get(action).toJS();
      realAction = mapping.action;
      append = mapping.append;
    } else {
      // Reverse lookup: is there an append action that maps to this action?
      // E.g., user says 'next', check if cursor has 'append_next' which maps to 'next'
      const appendActionKey = appendActions.findKey(v => v.get('action') === action);
      if (appendActionKey && cursor.actions.has(appendActionKey)) {
        realAction = appendActionKey;
        append = appendActions.getIn([appendActionKey, 'append']);
      }
    }

    // Handle cursors representing pages in the old, integer-based
    // pagination API
    if (cursor.meta.get('usingOldPaginationAPI', false)) {
      return dispatch(loadEntries(collection, cursor.data.get('nextPage')));
    }
    try {
      dispatch(entriesLoading(collection));
      console.log(`[traverseCollectionCursor] Calling traverseCursor with action: ${action}, realAction: ${realAction}, cursor actions: ${JSON.stringify(cursor.actions?.toArray())}`);
      const {
        entries,
        cursor: newCursor
      } = await traverseCursor(backend, cursor, realAction);
      console.log(`[traverseCollectionCursor] traverseCursor succeeded, got ${entries.length} entries, new cursor actions: ${JSON.stringify(newCursor.actions?.toArray())}`);
      const pagination = newCursor.meta?.get('page');
      return dispatch(entriesLoaded(collection, entries, pagination, addAppendActionsToCursor(newCursor), append));
    } catch (err) {
      console.error(err);
      dispatch(addNotification({
        message: {
          details: err,
          key: 'ui.toast.onFailToLoadEntries'
        },
        type: 'error',
        dismissAfter: 8000
      }));
      return Promise.reject(dispatch(entriesFailed(collection, err)));
    }
  };
}
function escapeHtml(unsafe) {
  return unsafe.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}
function processValue(unsafe) {
  if (['true', 'True', 'TRUE'].includes(unsafe)) {
    return true;
  }
  if (['false', 'False', 'FALSE'].includes(unsafe)) {
    return false;
  }
  return escapeHtml(unsafe);
}
function getDataFields(fields) {
  return fields.filter(f => !f.get('meta')).toList();
}
function getMetaFields(fields) {
  return fields.filter(f => f.get('meta') === true).toList();
}
export function createEmptyDraft(collection, search) {
  return async (dispatch, getState) => {
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      collection = updateFieldByKey(collection, key, field => field.set('default', processValue(value)));
    });
    const fields = collection.get('fields', List());
    const dataFields = getDataFields(fields);
    const data = createEmptyDraftData(dataFields);
    const metaFields = getMetaFields(fields);
    const meta = createEmptyDraftData(metaFields);
    const state = getState();
    const backend = currentBackend(state.config);
    if (!collection.has('media_folder')) {
      await waitForMediaLibraryToLoad(dispatch, getState());
    }
    const i18nFields = createEmptyDraftI18nData(collection, dataFields);
    let newEntry = createEntry(collection.get('name'), '', '', {
      data,
      i18n: i18nFields,
      mediaFiles: [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      meta: meta
    });
    newEntry = await backend.processEntry(state, collection, newEntry);
    dispatch(emptyDraftCreated(newEntry));
  };
}
export function createEmptyDraftData(fields, skipField = () => false) {
  return fields.reduce((reduction, value) => {
    const acc = reduction;
    const item = value;
    if (skipField(item)) {
      return acc;
    }
    const subfields = item.get('field') || item.get('fields');
    const list = item.get('widget') == 'list';
    const name = item.get('name');
    const defaultValue = item.get('default', null);
    function isEmptyDefaultValue(val) {
      return [[{}], {}].some(e => isEqual(val, e));
    }
    const hasSubfields = List.isList(subfields) || Map.isMap(subfields);
    if (hasSubfields) {
      if (list && List.isList(defaultValue)) {
        acc[name] = defaultValue;
      } else {
        const asList = List.isList(subfields) ? subfields : List([subfields]);
        const subDefaultValue = list ? [createEmptyDraftData(asList, skipField)] : createEmptyDraftData(asList, skipField);
        if (!isEmptyDefaultValue(subDefaultValue)) {
          acc[name] = subDefaultValue;
        }
      }
      return acc;
    }
    if (defaultValue !== null) {
      acc[name] = defaultValue;
    }
    return acc;
  }, {});
}
function createEmptyDraftI18nData(collection, dataFields) {
  if (!hasI18n(collection)) {
    return {};
  }
  function skipField(field) {
    return field.get(I18N) !== I18N_FIELD.DUPLICATE && field.get(I18N) !== I18N_FIELD.TRANSLATE;
  }
  const i18nData = createEmptyDraftData(dataFields, skipField);
  return duplicateDefaultI18nFields(collection, i18nData);
}
export function getMediaAssets({
  entry
}) {
  const filesArray = entry.get('mediaFiles').toArray();
  const assets = filesArray.filter(file => file.get('draft')).map(file => createAssetProxy({
    path: file.get('path'),
    file: file.get('file'),
    url: file.get('url'),
    field: file.get('field')
  }));
  return assets;
}
export function getSerializedEntry(collection, entry) {
  /**
   * Serialize the values of any fields with registered serializers, and
   * update the entry and entryDraft with the serialized values.
   */
  const fields = selectFields(collection, entry.get('slug'));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function serializeData(data) {
    return serializeValues(data, fields);
  }
  const serializedData = serializeData(entry.get('data'));
  let serializedEntry = entry.set('data', serializedData);
  if (hasI18n(collection)) {
    serializedEntry = serializeI18n(collection, serializedEntry, serializeData);
  }
  return serializedEntry;
}
export function persistEntry(collection) {
  return async (dispatch, getState) => {
    const state = getState();
    const entryDraft = state.entryDraft;
    const fieldsErrors = entryDraft.get('fieldsErrors');
    const usedSlugs = selectPublishedSlugs(state, collection.get('name'));

    // Early return if draft contains validation errors
    if (!fieldsErrors.isEmpty()) {
      const hasPresenceErrors = fieldsErrors.some(errors => errors.some(error => error.type && error.type === ValidationErrorTypes.PRESENCE));
      if (hasPresenceErrors) {
        dispatch(addNotification({
          message: {
            key: 'ui.toast.missingRequiredField'
          },
          type: 'error',
          dismissAfter: 8000
        }));
      }
      return Promise.reject();
    }
    const backend = currentBackend(state.config);
    const entry = entryDraft.get('entry');
    const assetProxies = getMediaAssets({
      entry
    });
    const serializedEntry = getSerializedEntry(collection, entry);
    const serializedEntryDraft = entryDraft.set('entry', serializedEntry);
    dispatch(entryPersisting(collection, serializedEntry));
    return backend.persistEntry({
      config: state.config,
      collection,
      entryDraft: serializedEntryDraft,
      assetProxies,
      usedSlugs
    }).then(async newSlug => {
      // Invalidate cache for this collection
      await invalidateCollectionCache(collection.get('name'));
      dispatch(addNotification({
        message: {
          key: 'ui.toast.entrySaved'
        },
        type: 'success',
        dismissAfter: 4000
      }));

      // re-load media library if entry had media files
      if (assetProxies.length > 0) {
        await dispatch(loadMedia());
      }

      // Create entry with updated slug for cache update
      const persistedEntry = newSlug !== serializedEntry.get('slug') ? serializedEntry.set('slug', newSlug) : serializedEntry;
      dispatch(entryPersisted(collection, persistedEntry, newSlug));
      if (collection.has('nested')) {
        await dispatch(loadEntries(collection));
      }
      if (entry.get('slug') !== newSlug) {
        await dispatch(loadEntry(collection, newSlug));
        navigateToEntry(collection.get('name'), newSlug);
      }
    }).catch(error => {
      console.error(error);
      dispatch(addNotification({
        message: {
          details: error,
          key: 'ui.toast.onFailToPersist'
        },
        type: 'error',
        dismissAfter: 8000
      }));
      return Promise.reject(dispatch(entryPersistFail(collection, serializedEntry, error)));
    });
  };
}
export function deleteEntry(collection, slug) {
  return (dispatch, getState) => {
    const state = getState();
    const backend = currentBackend(state.config);
    dispatch(entryDeleting(collection, slug));
    return backend.deleteEntry(state, collection, slug).then(async () => {
      // Invalidate cache for this collection
      await invalidateCollectionCache(collection.get('name'));
      return dispatch(entryDeleted(collection, slug));
    }).catch(error => {
      dispatch(addNotification({
        message: {
          details: error,
          key: 'ui.toast.onFailToDelete'
        },
        type: 'error',
        dismissAfter: 8000
      }));
      console.error(error);
      return Promise.reject(dispatch(entryDeleteFail(collection, slug, error)));
    });
  };
}
function getPathError(path, key, t) {
  return {
    error: {
      type: ValidationErrorTypes.CUSTOM,
      message: t(`editor.editorControlPane.widget.${key}`, {
        path
      })
    }
  };
}
function getExistingEntry(entries, collection, path, path_type) {
  const customPath = selectCustomPath(collection, fromJS({
    entry: {
      meta: {
        path,
        path_type
      }
    }
  }));
  if (!customPath) {
    return undefined;
  }
  return selectEntryByPath(entries, collection.get('name'), customPath);
}
export function validateMetaField(state, collection, field, value, t) {
  if (field.get('meta') && field.get('name') === 'path') {
    if (!value) {
      return getPathError(value, 'invalidPath', t);
    }
    const sanitizedPath = value.split('/').map(getProcessSegment(state.config.slug)).join('/');
    if (value !== sanitizedPath) {
      return getPathError(value, 'invalidPath', t);
    }
    const existingEntry = getExistingEntry(state.entries, collection, value, 'index') ?? getExistingEntry(state.entries, collection, value, 'slug');
    const existingEntryPath = existingEntry?.get('path');
    const draftPath = state.entryDraft?.getIn(['entry', 'path']);
    if (existingEntryPath && existingEntryPath !== draftPath) {
      return getPathError(value, 'pathExists', t);
    }
  }
  return {
    error: false
  };
}